/*
 *  saga to load auth user details from the just token
 */
import React from 'react'
import {
  put,
  takeLatest,
  all,
  delay,
  select,
  takeEvery,
  fork,
  cancel,
} from 'redux-saga/effects'
import Axios from 'axios'

import { fetchAlerts } from '../../state/actions/alerts'
import { translate } from '../../utils/translation'
import { getDomain } from '../../utils/functions'
import { getMaxValuesForUser } from '../../utils/freeVersionBlock'
import {
  startSiteAudit as startSiteAuditAPI,
  getPageSpeedInsight,
  queryPagesDiscovered,
  querySiteAuditBreakdown,
  queryPageOverview,
  queryDomainOverview,
  querySiteAudit,
  stopSiteAudit,
  updateIgnoredIssues,
  startPartialSiteAudit,
  queryPartialSiteAudit,
  getPageSpeedInsightFromBE,
  DEFAULT_NUMBER_OF_API_CALL_RETRIES,
} from '../../utils/api'
import { Text } from '../../components/topography'
import { palette } from '../../styles/variables'
import {
  AUDIT_POLL_DELAY,
  AUDIT_POLL_DELAY_INTERVAL,
  ISSUES_BREAKDOWN_URL_MISMATCH_ERROR,
} from '../../utils/consts.mjs'
import {
  removeSiteAuditCronjob,
  saveSiteAuditCronjob,
} from '../../utils/storage'
import { Mixpanel } from '../../utils/mixpanel'
import { selectCurrentUser } from '../../state/selectors'

import { SiteAuditErrorNote } from './styled'
import {
  selectIssueBreakdown,
  selectIssueIgnored,
  selectIsCrawling,
  selectStopCrawling,
  selectSiteAuditState,
  selectProjectStopCrawling,
  selectProjectIsCrawling,
} from './selectors'
import {
  CRAWL_SITE_AUDIT,
  GET_LIGHTHOUSE_BENCHMARK,
  getLighthouseBenchMarkActionSuccess,
  getLighthouseBenchMarkActionFailed,
  GET_PAGES_DISCOVERED,
  getPagesDiscoveredActionSuccess,
  getPagesDiscoveredActionFailed,
  GET_ISSUE_BREAKDOWN,
  GET_OVERVIEW,
  ADD_NOTIFICATION,
  STOP_SITE_AUDIT,
  GET_SITE_AUDIT_DATA,
  getOverviewAction,
  getOverviewActionSuccess,
  getOverviewActionFailed,
  crawlSiteAuditAction,
  crawlSiteAuditActionFailed,
  crawlSiteAuditDataActionPolling,
  removeNotificationAction,
  stopSiteAuditActionSuccess,
  getIssueBreakdownActionSuccess,
  getIssueBreakdownActionFailed,
  crawlSiteAuditActionSuccess,
  UPDATE_ISSUES,
  CRAWL_PARTIAL_SITE_AUDIT,
  crawlPartialSiteAuditActionFailed,
  crawlPartialSiteAuditActionPolling,
  crawlPartialSiteAuditActionSuccess,
  displayUpgradeModalAction,
  GET_PROJECT_SITE_AUDIT_DATA,
  crawlProjectSiteAuditActionSuccess,
  crawlProjectSiteAuditDataActionPolling,
  crawlProjectSiteAuditActionFailed,
  getProjectOverviewAction,
  getProjectOverviewActionSuccess,
  getProjectOverviewActionFailed,
  GET_PROJECT_LIGHTHOUSE_BENCHMARK,
  getProjectLighthouseBenchMarkActionSuccess,
  getProjectLighthouseBenchMarkActionFailed,
  GET_PROJECT_OVERVIEW,
} from './actions'

const getNonFriendlyIssues = (values, domain, type) => {
  const [nonFriendlyURL, ...checks] = values.map((res) =>
    !res.data.result[type] ? [] : res.data.result[type],
  )
  const urls = nonFriendlyURL.map(
    (data) => data.url || `${getDomain(domain)}${data.path || ''}`,
  )
  const result = urls.map((url) => ({
    url,
    value: checks.map(
      (check) => !check.some((innerCheck) => url === innerCheck.url),
    ),
  }))

  return result
}

let siteAuditCancelFunc
let siteAuditPollingTask
let pageSpeecBECancelFunc
let pageSpeedBEPollingTask

let projectSiteAuditCancelFunc
let projectSiteAuditPollingTask
let projectPageSpeecBECancelFunc
let projectPageSpeedBEPollingTask

export default function* siteAuditSaga() {
  yield takeEvery(GET_SITE_AUDIT_DATA.action, (action) =>
    getSiteAuditDataHandler(
      action.siteAuditDomainType,
      action.domain,
      action.mode,
      action.useCache,
      action.locId,
      action.lang,
    ),
  )
  yield takeLatest(CRAWL_SITE_AUDIT.action, (action) =>
    startSiteAuditHandler(action.payload),
  )
  yield takeLatest(GET_LIGHTHOUSE_BENCHMARK.action, (action) =>
    getLighthouseBenchmarkHandler(action.payload),
  )
  yield takeLatest(GET_PAGES_DISCOVERED.action, (action) =>
    getPagesDiscoveredHandler(action.payload),
  )
  yield takeLatest(GET_ISSUE_BREAKDOWN.action, (action) =>
    getIssueBreakdownHandler(action.payload),
  )
  yield takeLatest(GET_OVERVIEW.action, (action) =>
    getOverviewHandler(action.payload),
  )

  yield takeLatest(STOP_SITE_AUDIT.action, (action) =>
    stopSiteAuditHandler(action.payload, action.onComplete),
  )
  yield takeEvery(ADD_NOTIFICATION.action, (action) =>
    addNotificationHandler(action.payload),
  )

  yield takeEvery(UPDATE_ISSUES.action, (action) =>
    updateIssuesHandler(action.payload, action.onComplete),
  )

  yield takeLatest(CRAWL_PARTIAL_SITE_AUDIT.action, (action) =>
    startPartialSiteAuditHandler(action.payload, action.onComplete),
  )

  yield takeLatest(GET_PROJECT_SITE_AUDIT_DATA.action, (action) =>
    getProjectSiteAuditDataHandler(action.payload),
  )
  yield takeLatest(GET_PROJECT_LIGHTHOUSE_BENCHMARK.action, (action) =>
    getProjectLighthouseBenchmarkHandler(action.payload),
  )
  yield takeLatest(GET_PROJECT_OVERVIEW.action, (action) =>
    getProjectOverviewHandler(action.payload),
  )
}

function* getSiteAuditDataHandler(type, domain, mode, useCache, locId, lang) {
  try {
    const user = yield select(selectCurrentUser())
    const siteAudit = yield select(selectSiteAuditState())
    if (
      siteAudit.domain &&
      siteAudit?.domain !== domain &&
      user.tier !== 'tier3'
    ) {
      yield put(displayUpgradeModalAction())
    }
    const isCrawling = yield select(selectIsCrawling())
    if (!isCrawling) {
      yield saveSiteAuditCronjob(domain, mode, locId, lang)
      yield put(
        crawlSiteAuditAction({
          type,
          domain,
          mode,
          useCache,
        }),
      )
      yield put(getOverviewAction({ domain, mode, useCache, locId, lang }))
      yield getLighthouseBenchmarkHandler({ domain, mode, useCache })
    } else {
      yield pollSiteAuditDataHandler({ domain, mode, useCache })
    }
  } catch (error) {
    console.error('error', error)
  }
}

function* startSiteAuditHandler({ domain, mode, useCache }) {
  try {
    yield stopPollingTasks()
    const { CRAWL_MAX_PAGES } = yield getMaxValuesForUser()
    yield startSiteAuditAPI(
      getDomain(domain),
      mode === 'url' ? getDomain(domain, 'pathname') : undefined,
      CRAWL_MAX_PAGES,
      false,
      !useCache,
      new Axios.CancelToken((c) => {
        siteAuditCancelFunc = c
      }),
    )
    siteAuditPollingTask = yield fork(pollSiteAuditDataHandler, {
      domain,
      mode,
      useCache,
    })
  } catch (error) {
    let payload
    if (error.message === 'cancel_crawling') {
      payload = {
        done: false,
        isCrawling: false,
        crawlCount: 0,
      }
    } else if (
      error.response &&
      error.response.data.description === 'Invalid domain provided'
    ) {
      payload = {
        searchBarError: translate('error_invalid_url_seo_analyzer'),
        done: false,
        isCrawling: false,
      }
    } else {
      payload = {
        crawlingErrorText: translate('progress_bar_error_message'),
        done: true,
        isCrawling: false,
      }
    }
    yield put(crawlSiteAuditActionFailed(payload))
  }
}

function* pollSiteAuditDataHandler({
  domain,
  mode,
  useCache = true,
  noAlert = false,
}) {
  let retry = DEFAULT_NUMBER_OF_API_CALL_RETRIES

  while (true) {
    try {
      const queryFunc = yield querySiteAudit(
        useCache,
        mode === 'url' ? true : noAlert,
      )
      const { CRAWL_MAX_PAGES } = yield getMaxValuesForUser()
      const res = yield queryFunc(
        getDomain(domain),
        mode === 'url' ? getDomain(domain, 'pathname') : undefined,
        CRAWL_MAX_PAGES,
        // eslint-disable-next-line no-loop-func
        new Axios.CancelToken((c) => {
          siteAuditCancelFunc = c
        }),
      )
      const { done } = res.data.result
      if (!done) {
        const { crawl_count, crawl_max_pages } = res.data.result
        const siteAuditData = res.data.result.report
        yield put(
          crawlSiteAuditDataActionPolling({
            siteAuditData,
            done,
            crawlCount: crawl_count,
            crawlingProgress: crawl_max_pages
              ? crawl_count / crawl_max_pages
              : 0,
          }),
        )
        yield delay(AUDIT_POLL_DELAY)
      } else {
        const { report } = res.data.result
        const siteAuditData = report
        const stopCrawling = yield select(selectStopCrawling())
        if (stopCrawling) return

        if (report.extended_status && report.extended_status !== 'no_errors') {
          yield put(
            crawlSiteAuditActionFailed({
              done,
              siteAuditData,
              crawlingErrorText: translate('progress_bar_error_message'),
              isCrawling: false,
            }),
          )
          yield addNotificationHandler({
            id: Date.now(),
            type: 'error',
            content: <SiteAuditErrorNote error={report.extended_status} />,
          })
        } else {
          yield put(
            crawlSiteAuditActionSuccess({
              crawlingProgress: 1,
              done,
              siteAuditData,
              isCrawling: false,
            }),
          )
          Mixpanel.trackSiteAuditSuccess(siteAuditData.overview.crawled)

          // do not update the alert if crawl is from the localstorage cache.
          if (!res.headers['x-from-cache']) {
            yield put(
              fetchAlerts({
                alertType: 'site_audit_report_created',
                domain,
              }),
            )
          }
        }
        yield removeSiteAuditCronjob()
        break
      }
    } catch (error) {
      const isCrawling = yield select(selectIsCrawling())
      if (!isCrawling) break
      const previousTokenError =
        error.response &&
        error.response.status === 403 &&
        error.response.data &&
        error.response.data.error === 'FORBIDDEN' &&
        error.response.data.description.startsWith('The token')

      if (error.response) {
        const remainingCount = parseInt(
          error.response.headers['x-ratelimit-remaining'] || '0',
          10,
        )
        if ((previousTokenError && remainingCount === 0) || retry === 0) {
          yield put(
            crawlSiteAuditActionFailed({
              done: true,
              siteAuditData: null,
              isCrawling: false,
            }),
          )
          break
        }
      }

      if (error.response && error.response.status === 400) {
        yield put(
          crawlSiteAuditActionFailed({
            crawlingErrorText: translate('progress_bar_error_message'),
            done: true,
            isCrawling: false,
          }),
        )

        yield addNotificationHandler({
          id: Date.now(),
          type: 'error',
          content: <SiteAuditErrorNote error="repeated_errors" />,
        })
        break
      } else if (error.response && error.response.status === 500) {
        yield put(
          crawlSiteAuditActionFailed({
            crawlingErrorText: translate('progress_bar_error_message'),
            done: true,
            isCrawling: false,
          }),
        )
        yield addNotificationHandler({
          id: Date.now(),
          type: 'error',
          content: <SiteAuditErrorNote error="unknown" />,
        })
        break
      }

      retry -= 1

      yield delay(AUDIT_POLL_DELAY)
    }
  }
}

function* stopPollingTasks() {
  if (siteAuditCancelFunc) {
    siteAuditCancelFunc('cancel_crawling')
    siteAuditCancelFunc = null
  }
  if (siteAuditPollingTask) {
    yield cancel(siteAuditPollingTask)
    siteAuditPollingTask = null
  }

  if (pageSpeedBEPollingTask) {
    yield cancel(pageSpeedBEPollingTask)
    pageSpeedBEPollingTask = null
  }
  if (pageSpeecBECancelFunc) {
    pageSpeecBECancelFunc('cancel_page_speed')
    pageSpeecBECancelFunc = null
  }
}

function* stopSiteAuditHandler({ domain, path }, onComplete) {
  try {
    yield stopPollingTasks()
    yield removeSiteAuditCronjob()

    yield stopSiteAudit(domain, path)

    yield put(
      stopSiteAuditActionSuccess({
        isCrawling: false,
        done: false,
        crawlingProgress: 0,
        crawlCount: 0,
      }),
    )
    if (onComplete) onComplete()
  } catch (error) {
    yield put(
      stopSiteAuditActionSuccess({
        isCrawling: false,
        done: false,
        crawlingProgress: 0,
        crawlCount: 0,
      }),
    )
    if (onComplete) onComplete()
  }
}

function* getLighthouseBenchmarkHandler({ domain, mode, useCache }) {
  try {
    const response = yield getPageSpeedInsight(useCache)(domain, mode)
    const failedDevices = []
    if (response.DESKTOP.error && response.DESKTOP.error.code === 429) {
      failedDevices.push('DESKTOP')
    }
    if (response.MOBILE.error && response.MOBILE.error.code === 429) {
      failedDevices.push('MOBILE')
    }

    if (failedDevices.length > 0) {
      pageSpeedBEPollingTask = yield fork(
        pollSiteSpeedFromBE,
        getDomain(domain, 'origin_url'),
        useCache,
        failedDevices,
        response,
      )
    } else {
      yield put(getLighthouseBenchMarkActionSuccess(response))
    }
  } catch (error) {
    yield put(
      getLighthouseBenchMarkActionFailed({
        error,
      }),
    )
  }
}

function* pollSiteSpeedFromBE(domain, useCache, devices, previousResponse) {
  let forceUpdate = !useCache
  while (true) {
    try {
      const response = yield getPageSpeedInsightFromBE(
        domain,
        forceUpdate,
        devices.join(','),
        new Axios.CancelToken((c) => {
          pageSpeecBECancelFunc = c
        }),
      )
      if (response.data.done) {
        const result = {
          ...previousResponse,
          ...response.data.report,
        }
        yield put(getLighthouseBenchMarkActionSuccess(result))
        break
      }
      forceUpdate = false
      yield delay(3000)
      const stopCrawling = yield select(selectStopCrawling())
      if (stopCrawling) break
    } catch (error) {
      yield put(getLighthouseBenchMarkActionFailed(error))
      break
    }
  }
}

function* getPagesDiscoveredHandler(info) {
  try {
    const { domain } = info
    const res = yield queryPagesDiscovered(domain)
    if (res.data) {
      const pagesDiscovered = res.data.data

      const pageCount = Math.ceil(pagesDiscovered.length / 50)
      const pagination = {
        page: 0,
        pageCount,
        pageSize: 50,
      }
      yield put(
        getPagesDiscoveredActionSuccess({
          isLoading: false,
          data: pagesDiscovered,
          pagination,
          last_crawled: res.data.last_crawled_date,
        }),
      )
    }
  } catch (error) {
    yield put(getPagesDiscoveredActionFailed(error))
  }
}

function* getIssueBreakdownHandler({
  domain,
  mode,
  issue,
  useCache,
  hideIssues,
}) {
  try {
    if (issue === 'seo_non_friendly_url') {
      const values = yield all([
        ...['seo_non_friendly_url', ...hideIssues].map((issueId) =>
          querySiteAuditBreakdown(useCache)(
            getDomain(domain),
            mode === 'url' ? getDomain(domain, 'pathname') : undefined,
            issueId,
          ),
        ),
      ])
      const issueBreakdown = getNonFriendlyIssues(values, domain, 'breakdown')
      const issueIgnored = getNonFriendlyIssues(values, domain, 'ignored')
      const oldIssueBreakdown = yield select(selectIssueBreakdown())
      const oldIssueIgnored = yield select(selectIssueIgnored())
      yield put(
        getIssueBreakdownActionSuccess({
          issueBreakdown: {
            ...oldIssueBreakdown,
            [issue]: issueBreakdown,
          },
          issueIgnored: {
            ...oldIssueIgnored,
            [issue]: issueIgnored,
          },
          issueDiff: values.diff,
        }),
      )
    } else {
      const res = yield querySiteAuditBreakdown(useCache)(
        getDomain(domain),
        mode === 'url' ? getDomain(domain, 'pathname') : undefined,
        issue,
      )
      let breakdown = []
      let ignored = []

      if (res.data.result && res.data.result.breakdown) {
        breakdown = res.data.result.breakdown.map((mappedBreakdown) => ({
          ...mappedBreakdown,
          originalUrl: mappedBreakdown.url,
          url:
            mappedBreakdown.url ||
            `http://${getDomain(domain)}${mappedBreakdown.path || ''}`,
        }))
      }

      if (res.data.result && res.data.result.ignored) {
        ignored = res.data.result.ignored.map((mappedIgnored) => ({
          ...mappedIgnored,
          originalUrl: mappedIgnored.url,
          url:
            mappedIgnored.url ||
            `http://${getDomain(domain)}${mappedIgnored.path || ''}`,
        }))
      }
      const oldIssueBreakdown = yield select(selectIssueBreakdown())
      const oldIssueIgnored = yield select(selectIssueIgnored())
      yield put(
        getIssueBreakdownActionSuccess({
          issueBreakdown: {
            ...oldIssueBreakdown,
            [issue]: breakdown,
          },
          issueIgnored: {
            ...oldIssueIgnored,
            [issue]: ignored,
          },
          issueDiff: res.data.result.diff,
        }),
      )
    }
  } catch (error) {
    console.error(error)
    yield put(
      getIssueBreakdownActionFailed({
        issueBreakdown: {},
        issueIgnored: {},
        issueDiff: {},
      }),
    )
  }
}

function* getOverviewHandler({ domain, mode, locId, lang }) {
  try {
    const queryFunc = mode === 'url' ? queryPageOverview : queryDomainOverview
    const res = yield queryFunc(
      getDomain(domain, mode === 'url' ? 'origin_url' : mode),
      locId,
      lang,
      false,
    )
    const { organic, traffic, backlinks } = res.data
    yield put(
      getOverviewActionSuccess({
        organic,
        traffic,
        backlinks,
      }),
    )
  } catch (error) {
    yield put(getOverviewActionFailed(error))
  }
}

function* addNotificationHandler(payload) {
  yield delay(1000 * 1000)
  yield put(removeNotificationAction(payload))
}

function* updateAuditIssues() {
  const siteAudit = yield select(selectSiteAuditState())
  yield getIssueBreakdownHandler({
    domain: siteAudit.domain,
    mode: siteAudit.mode,
    issue: siteAudit.issue,
    useCache: false,
    hideIssues: siteAudit.hideIssues,
  })
  yield pollSiteAuditDataHandler({
    domain: siteAudit.domain,
    mode: siteAudit.mode,
    useCache: false,
    noAlert: true,
  })
}

function* updateIssuesHandler(payload, onComplete) {
  try {
    yield updateIgnoredIssues(payload)
    yield updateAuditIssues()
    if (onComplete) onComplete({ data: true })
  } catch (error) {
    try {
      if (
        error.response?.data?.description ===
        ISSUES_BREAKDOWN_URL_MISMATCH_ERROR
      ) {
        yield updateAuditIssues()
        if (onComplete) onComplete({ data: true })
      } else if (onComplete) onComplete({ error })
    } catch (err) {
      if (onComplete) onComplete({ err })
    }
    if (onComplete) onComplete({ error })
  }
}

function* startPartialSiteAuditHandler({ domain, urls, issueId }, onComplete) {
  try {
    yield startPartialSiteAudit(domain, urls, issueId)

    const { CRAWL_MAX_PAGES } = yield getMaxValuesForUser()

    yield pollPartialSiteAuditDataHandler(
      {
        domain,
        count: CRAWL_MAX_PAGES,
      },
      onComplete,
    )
  } catch (error) {
    yield put(
      crawlPartialSiteAuditActionFailed({
        isRecrawlingIssue: false,
      }),
    )
    if (onComplete) {
      onComplete({
        error: (
          <Text
            dangerouslySetInnerHTML={{
              __html: error.response.data.description,
            }}
            boldColor={palette.darkGray}
          />
        ),
      })
    }
  }
}

function* pollPartialSiteAuditDataHandler({ domain, count }, onComplete) {
  try {
    const res = yield queryPartialSiteAudit(domain, count)
    const { done } = res.data.result

    if (!done) {
      const { crawl_count, crawl_max_pages } = res.data.result
      const siteAuditData = res.data.result.report
      yield put(
        crawlPartialSiteAuditActionPolling({
          data: siteAuditData,
          done,
          crawlCount: crawl_count,
          crawlPages: crawl_max_pages,
          crawlingProgress: crawl_max_pages ? crawl_count / crawl_max_pages : 0,
        }),
      )
      yield delay(AUDIT_POLL_DELAY_INTERVAL)
      yield pollPartialSiteAuditDataHandler(
        {
          domain,
          count,
        },
        onComplete,
      )
    } else {
      const { report } = res.data.result
      const siteAuditData = report

      if (report.extended_status && report.extended_status !== 'no_errors') {
        yield put(
          crawlPartialSiteAuditActionFailed({
            done,
            data: siteAuditData,
            crawlingErrorText: translate('progress_bar_error_message'),
            isRecrawlingIssue: false,
          }),
        )

        if (onComplete) {
          onComplete({ error: report.extended_status })
        }
      } else {
        const siteAudit = yield select(selectSiteAuditState())
        yield getIssueBreakdownHandler({
          domain: siteAudit.domain,
          mode: siteAudit.mode,
          issue: siteAudit.issue,
          useCache: false,
          hideIssues: siteAudit.hideIssues,
        })
        yield put(
          crawlPartialSiteAuditActionSuccess({
            partialSiteAudit: {
              isRecrawlingIssue: false,
              crawlingProgress: 1,
              done,
              data: siteAuditData,
            },
            siteAuditData,
          }),
        )
        if (onComplete) {
          onComplete({ data: true })
        }
      }
    }
  } catch (error) {
    const previousTokenError =
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.error === 'FORBIDDEN' &&
      error.response.data.description.startsWith('The token')
    let errorMessage = ''
    if (error.response) {
      const remainingCount = parseInt(
        error.response.headers['x-ratelimit-remaining'] || '0',
        10,
      )
      if (previousTokenError && remainingCount === 0) {
        yield put(
          crawlPartialSiteAuditActionFailed({
            done: true,
            data: null,
            isRecrawlingIssue: false,
          }),
        )
        return
      }
    }

    if (error.response && error.response.status === 400) {
      errorMessage = 'repeated_errors'
    } else if (error.response && error.response.status === 500) {
      errorMessage = 'unknown'
    }
    yield put(
      crawlPartialSiteAuditActionFailed({
        crawlingErrorText: translate('progress_bar_error_message'),
        done: true,
        isRecrawlingIssue: false,
      }),
    )
    onComplete({ error: errorMessage })
  }
}

function* getProjectSiteAuditDataHandler({
  domain,
  mode,
  useCache,
  locId,
  lang,
}) {
  try {
    yield startProjectSiteAuditHandler({ domain, mode, useCache })

    yield put(getProjectOverviewAction({ domain, mode, useCache, locId, lang }))
    yield getProjectLighthouseBenchmarkHandler({ domain, mode, useCache })
  } catch (error) {
    console.error('error', error)
  }
}

function* startProjectSiteAuditHandler({ domain, mode, useCache }) {
  try {
    yield stopProjectPollingTasks()
    siteAuditPollingTask = yield fork(pollProjectSiteAuditDataHandler, {
      domain,
      mode,
      useCache,
      noAlert: true,
    })
  } catch (error) {
    let payload
    if (error.message === 'cancel_crawling') {
      payload = {
        done: false,
        isCrawling: false,
        crawlCount: 0,
      }
    } else if (
      error.response &&
      error.response.data.description === 'Invalid domain provided'
    ) {
      payload = {
        searchBarError: translate('error_invalid_url_seo_analyzer'),
        done: false,
        isCrawling: false,
      }
    } else {
      payload = {
        crawlingErrorText: translate('progress_bar_error_message'),
        done: true,
        isCrawling: false,
      }
    }
    yield put(crawlSiteAuditActionFailed(payload))
  }
}

function* pollProjectSiteAuditDataHandler({
  domain,
  useCache = true,
  noAlert = true,
}) {
  let retry = DEFAULT_NUMBER_OF_API_CALL_RETRIES

  while (true) {
    try {
      const queryFunc = yield querySiteAudit(useCache, noAlert)
      const res = yield queryFunc(
        getDomain(domain),
        undefined,
        new Axios.CancelToken((c) => {
          projectSiteAuditCancelFunc = c
        }),
      )
      const { done } = res.data.result
      if (!done) {
        const { crawl_count, crawl_max_pages } = res.data.result
        const siteAuditData = res.data.result.report
        yield put(
          crawlProjectSiteAuditDataActionPolling({
            siteAuditData,
            done,
            crawlCount: crawl_count,
            crawlingProgress: crawl_max_pages
              ? crawl_count / crawl_max_pages
              : 0,
          }),
        )
        yield delay(AUDIT_POLL_DELAY)
      } else {
        const { report } = res.data.result
        const siteAuditData = report
        const stopCrawling = yield select(selectProjectStopCrawling())
        if (stopCrawling) return

        if (report.extended_status && report.extended_status !== 'no_errors') {
          yield put(
            crawlProjectSiteAuditActionFailed({
              done,
              siteAuditData,
              crawlingErrorText: translate('progress_bar_error_message'),
              isCrawling: false,
            }),
          )
          yield addNotificationHandler({
            id: Date.now(),
            type: 'error',
            content: <SiteAuditErrorNote error={report.extended_status} />,
          })
        } else {
          yield put(
            crawlProjectSiteAuditActionSuccess({
              crawlingProgress: 1,
              done,
              siteAuditData,
              isCrawling: false,
            }),
          )

          if (!res.headers['x-from-cache']) {
            yield put(
              fetchAlerts({
                alertType: 'site_audit_report_created',
                domain,
              }),
            )
          }
        }
        break
      }
    } catch (error) {
      const isCrawling = yield select(selectProjectIsCrawling())
      if (!isCrawling) break
      const previousTokenError =
        error.response &&
        error.response.status === 403 &&
        error.response.data &&
        error.response.data.error === 'FORBIDDEN' &&
        error.response.data.description.startsWith('The token')

      if (error.response) {
        const remainingCount = parseInt(
          error.response.headers['x-ratelimit-remaining'] || '0',
          10,
        )
        if ((previousTokenError && remainingCount === 0) || retry === 0) {
          yield put(
            crawlProjectSiteAuditActionFailed({
              done: true,
              siteAuditData: null,
              isCrawling: false,
            }),
          )
          break
        }
      }

      if (error.response && error.response.status === 400) {
        yield put(
          crawlProjectSiteAuditActionFailed({
            crawlingErrorText: translate('progress_bar_error_message'),
            done: true,
            isCrawling: false,
            isCancelledCrawling: true, // set as cancelled if there's no report for project domain
          }),
        )

        yield addNotificationHandler({
          id: Date.now(),
          type: 'error',
          content: <SiteAuditErrorNote error="repeated_errors" />,
        })
        break
      } else if (error.response && error.response.status === 500) {
        yield put(
          crawlProjectSiteAuditActionFailed({
            crawlingErrorText: translate('progress_bar_error_message'),
            done: true,
            isCrawling: false,
          }),
        )
        yield addNotificationHandler({
          id: Date.now(),
          type: 'error',
          content: <SiteAuditErrorNote error="unknown" />,
        })
        break
      }

      retry -= 1

      yield delay(AUDIT_POLL_DELAY)
    }
  }
}

function* stopProjectPollingTasks() {
  if (projectSiteAuditCancelFunc) {
    projectSiteAuditCancelFunc('cancel_crawling')
    projectSiteAuditCancelFunc = null
  }
  if (projectSiteAuditPollingTask) {
    yield cancel(projectSiteAuditPollingTask)
    projectSiteAuditPollingTask = null
  }
  if (projectPageSpeedBEPollingTask) {
    yield cancel(projectPageSpeedBEPollingTask)
    projectPageSpeedBEPollingTask = null
  }
  if (projectPageSpeecBECancelFunc) {
    projectPageSpeecBECancelFunc('cancel_page_speed')
    projectPageSpeecBECancelFunc = null
  }
}

function* getProjectLighthouseBenchmarkHandler({ domain, mode, useCache }) {
  try {
    const response = yield getPageSpeedInsight(useCache)(domain, mode)
    const failedDevices = []
    if (response.DESKTOP.error && response.DESKTOP.error.code === 429) {
      failedDevices.push('DESKTOP')
    }
    if (response.MOBILE.error && response.MOBILE.error.code === 429) {
      failedDevices.push('MOBILE')
    }

    if (failedDevices.length > 0) {
      projectPageSpeedBEPollingTask = yield fork(
        pollProjectSiteSpeedFromBE,
        getDomain(domain, 'origin_url'),
        useCache,
        failedDevices,
        response,
      )
    } else {
      yield put(getProjectLighthouseBenchMarkActionSuccess(response))
    }
  } catch (error) {
    yield put(
      getProjectLighthouseBenchMarkActionFailed({
        error,
      }),
    )
  }
}

function* pollProjectSiteSpeedFromBE(
  domain,
  useCache,
  devices,
  previousResponse,
) {
  let forceUpdate = !useCache
  while (true) {
    try {
      const response = yield getPageSpeedInsightFromBE(
        domain,
        forceUpdate,
        devices.join(','),
        // eslint-disable-next-line no-loop-func
        new Axios.CancelToken((c) => {
          projectPageSpeecBECancelFunc = c
        }),
      )
      if (response.data.done) {
        const result = {
          ...previousResponse,
          ...response.data.report,
        }
        yield put(getProjectLighthouseBenchMarkActionSuccess(result))
        break
      }
      forceUpdate = false
      yield delay(3000)
      const stopCrawling = yield select(selectProjectStopCrawling())
      if (stopCrawling) break
    } catch (error) {
      yield put(getProjectLighthouseBenchMarkActionFailed(error))
      break
    }
  }
}

function* getProjectOverviewHandler({ domain, mode, locId, lang }) {
  try {
    const queryFunc = queryDomainOverview
    const res = yield queryFunc(getDomain(domain, mode), locId, lang, false)
    const { organic, traffic, backlinks } = res.data
    yield put(
      getProjectOverviewActionSuccess({
        organic,
        traffic,
        backlinks,
      }),
    )
  } catch (error) {
    yield put(getProjectOverviewActionFailed(error))
  }
}
